import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Upload, Video, ChevronUp, BarChart3, Settings, Film, Music } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { startTusUpload } from '../utils/tusUploader';
import { ArtistAnalyticsPage } from './ArtistAnalyticsPage';
import { ProfilePage } from './ProfilePage';
import { initialDisclosure, isDisclosureComplete, useToolsCatalog, RetagDisclosureModal } from '../components/AIDisclosure';
import { OversizeFileWarning } from '../components/OversizeFileWarning';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';
import { UPLOAD_STAGES, newUploadSessionId, logUploadEvent } from '../utils/uploadEvents';
import { FilmUploadForm } from '../components/studio/FilmUploadForm';
import { EditDetailsModal } from '../components/studio/EditDetailsModal';
import { EYE_ICON_URL } from '../components/studio/studioShared';
import { StudioMusicUploadForm } from '../components/studio/StudioMusicUploadForm';
import { StudioVideoGrid } from '../components/studio/StudioVideoGrid';

export const StudioPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadKind, setUploadKind] = useState('music'); // 'music' | 'film'
  const [musicSuggestedTags, setMusicSuggestedTags] = useState([]);
  // Session id for the current upload attempt. Minted when the user opens
  // the Upload panel (attempt_started); reset when they close and reopen.
  // All five upload_events for a single attempt share this id so the admin
  // funnel can dedupe retries from separate attempts.
  const [uploadSessionId, setUploadSessionId] = useState(null);
  const [tab, setTab] = useState('videos'); // 'videos' | 'analytics' | 'settings'
  const [searchParams] = useSearchParams();

  // Open the tab requested in `?tab=settings` (or analytics) so the mobile
  // bottom-nav "Account" link can deep-link straight into the Settings tab.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'settings' || t === 'analytics' || t === 'videos') {
      setTab(t);
    }
  }, [searchParams]);
  const { axiosInstance, user } = useAuth();
  const { playVideo } = usePlayer();
  const navigate = useNavigate();
  
  // Earnings state
  const [earnings, setEarnings] = useState({
    estimated_earnings: 0,
    available_balance: 0,
    pending_payout: 0,
    total_paid: 0,
    minimum_payout: 10
  });

  // Upload state
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [videoStyle, setVideoStyle] = useState('');
  const [toolsUsed, setToolsUsed] = useState('');
  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  // Oversize-warning modal state — see <OversizeFileWarning>. Deferred so the
  // user has a chance to pick a smaller export, but non-blocking.
  const [oversizeWarning, setOversizeWarning] = useState(null); // null | { file, sizeMB, durationSec, estBitrateKbps, width, height }
  
  // Artist name is derived from user profile - cannot be edited per video
  const artistName = user?.display_name || user?.username || user?.email?.split('@')[0] || 'Artist';
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnailTimestamp, setThumbnailTimestamp] = useState(1.0);
  const [capturedThumbnail, setCapturedThumbnail] = useState(null);
  // Mobile fallback: if the user picks a custom image (e.g. from camera roll
  // because client-side canvas capture is failing on iOS), we send THIS file
  // as the thumbnail and ignore the timestamp.
  const [customThumbnailFile, setCustomThumbnailFile] = useState(null);
  // Tracks whether the creator explicitly chose the "Use frame at time" /
  // server-extracts path. We need this because that path INTENTIONALLY sets
  // customThumbnailFile = null and relies on the backend's ffmpeg fallback.
  // The Upload gate must allow this through, but block "customThumbnailFile
  // still null because the async capture race lost it".
  const [useTimestampOnlySelected, setUseTimestampOnlySelected] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState(null); // 'verify' | 'audio' | 'compress' | 'finalize'
  const [processingElapsed, setProcessingElapsed] = useState(0);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [contentCertified, setContentCertified] = useState(false);
  const [disclosure, setDisclosure] = useState(initialDisclosure());
  const [retagVideo, setRetagVideo] = useState(null);
  const [editDetailsVideo, setEditDetailsVideo] = useState(null);
  const toolsCatalog = useToolsCatalog(axiosInstance);
  const videoInputRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);
  const customThumbnailInputRef = useRef(null);

  const fetchEarnings = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/creator/earnings');
      setEarnings(res.data);
    } catch (err) {
      console.error('Failed to fetch earnings:', err);
    }
  }, [axiosInstance]);

  const fetchMyVideos = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/creator/my-videos');
      setVideos(res.data.filter(v => !v.is_deleted));
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { 
    fetchMyVideos(); 
    fetchEarnings();
  }, [fetchMyVideos, fetchEarnings]);

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    try {
      await axiosInstance.delete(`/videos/${videoId}`);
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete video');
    }
  };

  // "Try to recover" — for videos in status=failed. Asks the backend to look
  // up this video's title in the Bunny library; if Bunny has a matching
  // unlinked video, we relink it and flip the row back to approved. Cures
  // the common case where Bunny's create-video succeeded on their side but
  // our client-side response parse failed (the video ended up on Bunny but
  // AUDIVUE showed UPLOAD FAILED).
  const [reconcilingId, setReconcilingId] = useState(null);
  const handleReconcile = async (video) => {
    if (reconcilingId) return;
    setReconcilingId(video.id);
    try {
      const res = await axiosInstance.post(`/videos/${video.id}/reconcile-with-bunny`);
      toast.success(res.data?.message || 'Recovered from Bunny.');
      // Merge the freshly-linked fields into the local card so the failed
      // banner disappears immediately (no need to refetch the whole list).
      setVideos(prev => prev.map(v => v.id === video.id ? {
        ...v,
        status: 'approved',
        bunny_video_guid: res.data?.bunny_video_guid,
        hls_url: res.data?.hls_url,
        processing_error: null,
      } : v));
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 404) {
        toast.error('No matching video found on Bunny — please re-upload.');
      } else {
        toast.error(detail || 'Recovery failed. Please try uploading again.');
      }
    } finally {
      setReconcilingId(null);
    }
  };

  const getThumbnail = (video) => {
    return resolveThumbnailUrl(video);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Log FILE_SELECTED as early as possible — even if this file later
      // fails the 720p / duration / size checks, we still want to know
      // the creator got this far. Failure of those checks fires
      // upload_failed with a client-side error_type.
      if (uploadSessionId) {
        logUploadEvent({
          axiosInstance,
          stage: UPLOAD_STAGES.FILE_SELECTED,
          sessionId: uploadSessionId,
          fileMetadata: { name: file.name, size: file.size, type: file.type },
        });
      }
      // Strict video file type validation
      const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
      if (!validTypes.includes(file.type) && !file.type.startsWith('video/')) { 
        toast.error('Please select a valid video file (MP4, MOV, or WebM)'); 
        return; 
      }
      // Hard limit: 2 GB (chunked upload bypasses the 100MB Cloudflare cap by
      // splitting into 5MB chunks, each well under the proxy limit).
      const MAX_SIZE_MB = 2048;
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_SIZE_MB) {
        toast.error(
          `File too large (${fileSizeMB.toFixed(0)}MB). AUDIVUE accepts videos up to ${MAX_SIZE_MB}MB.`,
          { duration: 8000 }
        );
        if (videoInputRef.current) videoInputRef.current.value = '';
        return;
      }

      // Client-side 720p check — saves users from uploading sub-720p videos
      const probeUrl = URL.createObjectURL(file);
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.muted = true;
      probe.src = probeUrl;
      probe.onloadedmetadata = () => {
        const w = probe.videoWidth;
        const h = probe.videoHeight;
        const dur = probe.duration || 0;
        URL.revokeObjectURL(probeUrl);
        if (w && h && Math.min(w, h) < 720) {
          toast.error(
            `Video is ${w}×${h}. AUDIVUE requires at least 720p (720 px on the short side). Please re-export at higher resolution.`,
            { duration: 8000 }
          );
          if (videoInputRef.current) videoInputRef.current.value = '';
          return;
        }
        if (dur && dur < 60) {
          toast.error(
            `Video is only ${Math.round(dur)}s long. AUDIVUE requires a complete song (at least 60 seconds).`,
            { duration: 8000 }
          );
          if (videoInputRef.current) videoInputRef.current.value = '';
          return;
        }

        // Oversize-bitrate guard: a well-encoded 1080p30 file at 3-5 Mbps for
        // 3 minutes is ~90 MB. Anything materially larger means the artist is
        // uploading a near-source-quality master that will:
        //   • take 5-10× longer to upload (esp. on mobile cellular)
        //   • burn server CPU re-compressing
        //   • potentially stutter on playback before re-compression finishes
        // Threshold: 150 MB OR an estimated bitrate > 8 Mbps. Show a modal
        // with export guidance, but let them proceed if they really want to.
        const estBitrateKbps = dur > 0 ? (file.size * 8) / dur / 1000 : 0;
        const fileMB = file.size / (1024 * 1024);
        const isOversize = fileMB > 150 || estBitrateKbps > 8000;
        if (isOversize) {
          setOversizeWarning({
            file,
            sizeMB: fileMB,
            durationSec: dur,
            estBitrateKbps,
            width: w,
            height: h,
          });
          // Important: do NOT setVideoFile here. The modal's Proceed button
          // is the one that commits the file.
          return;
        }

        // OK: actually accept the file
        setVideoFile(file);
        setVideoPreview(URL.createObjectURL(file));
        setCapturedThumbnail(null);
        setThumbnailTimestamp(1.0);
        setCurrentTime(0);
        setIsPlaying(false);
      };
      probe.onerror = () => {
        // Couldn't probe — fall back to letting the server validate
        URL.revokeObjectURL(probeUrl);
        setVideoFile(file);
        setVideoPreview(URL.createObjectURL(file));
        setCapturedThumbnail(null);
        setThumbnailTimestamp(1.0);
        setCurrentTime(0);
        setIsPlaying(false);
      };
    }
  };

  // Handle video metadata loaded
  const handleVideoLoaded = useCallback(() => {
    if (videoPreviewRef.current) {
      setVideoDuration(videoPreviewRef.current.duration);
      // Default thumbnail at 1 second or 10% of video
      const defaultTime = Math.min(1.0, videoPreviewRef.current.duration * 0.1);
      setThumbnailTimestamp(defaultTime);
    }
  }, []);

  // Update current time as video plays
  const handleTimeUpdate = useCallback(() => {
    if (videoPreviewRef.current) {
      setCurrentTime(videoPreviewRef.current.currentTime);
    }
  }, []);

  // Seek to specific time. On iOS Safari, seeking only works reliably while
  // the video is paused, so we pause before setting currentTime.
  const handleSeek = useCallback((e) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (videoPreviewRef.current) {
      if (!videoPreviewRef.current.paused) {
        try { videoPreviewRef.current.pause(); } catch (_e) { /* iOS teardown */ }
        setIsPlaying(false);
      }
      try { videoPreviewRef.current.currentTime = time; } catch (_e) { /* iOS teardown */ }
    }
  }, []);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (videoPreviewRef.current) {
      if (isPlaying) {
        videoPreviewRef.current.pause();
      } else {
        videoPreviewRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  // Capture current frame as thumbnail.
  // On mobile (iOS Safari especially), drawImage(video) can produce a black
  // frame if the video isn't fully decoded at the current time yet. We
  // explicitly wait for the next 'seeked' / video-frame callback before
  // drawing so the captured frame is the one the user sees.
  const captureFrame = useCallback(async () => {
    if (!videoPreviewRef.current || !canvasRef.current) return;

    const video = videoPreviewRef.current;
    const canvas = canvasRef.current;

    // Make sure we have a current frame decoded. readyState >= 2 means
    // HAVE_CURRENT_DATA (the current playback position has been decoded).
    // If not, nudge a seek to the current time and wait for it to complete.
    const waitForFrame = () => new Promise((resolve) => {
      if (video.readyState >= 2) {
        resolve();
        return;
      }
      const onReady = () => {
        video.removeEventListener('seeked', onReady);
        video.removeEventListener('loadeddata', onReady);
        resolve();
      };
      video.addEventListener('seeked', onReady, { once: true });
      video.addEventListener('loadeddata', onReady, { once: true });
      // Trigger a seek to force decode at current position
      try { video.currentTime = video.currentTime; } catch (_e) { /* iOS teardown */ }
      // Safety: don't hang forever if events never fire
      setTimeout(onReady, 1200);
    });

    try {
      await waitForFrame();
    } catch (err) {
      console.warn('[Studio] thumbnail capture: frame wait failed, drawing anyway:', err?.message || err);
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    if (!width || !height) {
      toast.error("Couldn't read video frame yet — wait a moment and try again.");
      return;
    }
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    try {
      ctx.drawImage(video, 0, 0, width, height);
    } catch (e) {
      toast.error("Couldn't capture this frame on mobile. Try playing the video for a second, then pausing and capturing.");
      return;
    }

    let thumbnailDataUrl = '';
    try {
      thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    } catch (e) {
      toast.error('Frame capture failed. Try a different point in the video.');
      return;
    }
    // Detect a fully-black frame (mobile fallback path) and tell the user.
    if (thumbnailDataUrl.length < 5000) {
      toast.error("Frame looks empty — play the video briefly, pause, then capture.");
      return;
    }
    // Pixel-level black-frame check. Many mobile browsers (iOS Safari, some
    // Android WebViews) refuse to decode a <video> into a canvas until the
    // video has actually been *played* — drawImage silently returns an
    // all-black canvas which encodes to a normal-sized but useless JPEG
    // (~15 KB at 1280x720). The dataURL.length heuristic above can't catch
    // that, so sample real pixels and reject if every sample is essentially
    // black. The captured "thumbnail" was overwriting custom_thumbnail_url
    // with a black image, which is why thumbnails came back missing on
    // Home + Studio even though the save path worked.
    try {
      const samples = 64;
      const data = ctx.getImageData(0, 0, width, height).data;
      let nonBlack = 0;
      let brightestSum = 0;
      for (let i = 0; i < samples; i += 1) {
        const px = Math.floor(Math.random() * (data.length / 4)) * 4;
        const r = data[px], g = data[px + 1], b = data[px + 2];
        const lum = r + g + b;
        brightestSum = Math.max(brightestSum, lum);
        if (lum > 24) nonBlack += 1; // luma threshold ≈ "anything visible"
      }
      if (nonBlack < 2 && brightestSum < 24) {
        toast.error("Frame is all black — tap Play for a second, pause on the frame you want, then capture again.");
        return;
      }
    } catch (_e) {
      // getImageData can throw on tainted canvases — fall through, the
      // existing safeguards already gate the obvious failure modes.
    }
    // Synchronously convert the canvas-captured dataURL into a real File and
    // stage it as `customThumbnailFile` BEFORE we mark the preview as ready.
    // Using the existing dataURL→Blob path (no canvas.toBlob async callback)
    // removes the race where the Upload button enables while customThumbnailFile
    // is still null, which silently sent finalize with no thumbnail and left
    // `custom_thumbnail_url` empty in the DB. (BUG 1 root cause — fixed.)
    let stagedThumbFile = null;
    try {
      const b64 = thumbnailDataUrl.split(',')[1] || '';
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'image/jpeg' });
      stagedThumbFile = new File([blob], `thumb-${Date.now()}.jpg`, { type: 'image/jpeg' });
    } catch (_e) {
      toast.error('Frame capture failed. Try a different point in the video.');
      return;
    }
    setCustomThumbnailFile(stagedThumbFile);
    setUseTimestampOnlySelected(false);
    setCapturedThumbnail(thumbnailDataUrl);
    setThumbnailTimestamp(video.currentTime);
    toast.success(`Frame captured at ${formatTime(video.currentTime)}`);
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const clearVideo = () => { 
    setVideoFile(null); 
    setVideoPreview(null); 
    setCapturedThumbnail(null);
    setCustomThumbnailFile(null);
    setUseTimestampOnlySelected(false);
    setVideoDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    if (videoInputRef.current) videoInputRef.current.value = ''; 
  };

  // Mobile fallback A — accept the user's hand-picked image file (camera roll,
  // gallery, or a still they shot). Bypasses the iOS Safari canvas-taint /
  // suspended-decoder issues that break the client-side capture flow.
  const handleCustomThumbnailPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pick an image file (JPG, PNG, WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5MB.');
      return;
    }
    setCustomThumbnailFile(file);
    setUseTimestampOnlySelected(false);
    setCapturedThumbnail(URL.createObjectURL(file));
    toast.success('Custom thumbnail set.');
  };

  // Mobile fallback B — skip the client-side canvas entirely. Just record the
  // current playhead time and let the server extract the frame from the
  // uploaded video. We set a tiny SVG placeholder so the UI shows "set" state
  // and the Upload button enables.
  const useTimestampOnly = () => {
    const video = videoPreviewRef.current;
    if (!video) return;
    const t = video.currentTime || 1.0;
    setThumbnailTimestamp(t);
    setCustomThumbnailFile(null);
    setUseTimestampOnlySelected(true);
    // Inline SVG placeholder — keeps the UI happy without doing any canvas work.
    const placeholder =
      'data:image/svg+xml;utf8,' + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180">` +
        `<rect width="320" height="180" fill="#18181b"/>` +
        `<text x="50%" y="44%" text-anchor="middle" fill="#a1a1aa" font-family="system-ui" font-size="14">Frame at ${formatTime(t)}</text>` +
        `<text x="50%" y="62%" text-anchor="middle" fill="#71717a" font-family="system-ui" font-size="11">Server will extract on upload</text>` +
        `</svg>`
      );
    setCapturedThumbnail(placeholder);
    toast.success(`Will use frame at ${formatTime(t)} (server extracts).`);
  };

  const resetUploadForm = () => {
    setTitle(''); setGenre(''); setVideoStyle(''); setToolsUsed(''); setDescription('');
    clearVideo();
    setRightsConfirmed(false); setContentCertified(false); setProgress(0);
    setProcessingStage(null); setProcessingElapsed(0);
    setDisclosure(initialDisclosure());
  };

  const handleUpload = async () => {
    if (!title || !genre || !videoFile) {
      toast.error('Please fill in all required fields'); return;
    }
    if (!capturedThumbnail) {
      toast.error('Please capture a thumbnail frame from your video'); return;
    }
    // Guard the race-condition path: if the creator captured a frame but
    // customThumbnailFile somehow didn't get staged (and they did NOT pick
    // "Use frame at time"), block instead of silently shipping a finalize
    // with no thumbnail file.
    if (!customThumbnailFile && !useTimestampOnlySelected) {
      toast.error('Thumbnail still processing — give it a second and try again.');
      return;
    }
    if (!isDisclosureComplete(disclosure)) {
      if (disclosure.none) {
        toast.error('AUDIVUE only accepts AI-generated or AI-assisted videos. Uncheck "None" and tell us what AI is in your video.');
      } else {
        toast.error('Please tell us what AI is in this video and which tool you used.');
      }
      return;
    }
    if (!rightsConfirmed) {
      toast.error('You must confirm that you own the rights to this video'); return;
    }
    if (!contentCertified) {
      toast.error('You must certify that your content complies with our guidelines'); return;
    }
    setUploading(true); setProgress(0); setProcessingStage(null); setProcessingElapsed(0);
    // Retry counter shared between the try success path and the catch
    // failure path so upload_failed events carry TUS retry metadata.
    // 0 = clean upload. n>0 = auto-recovered from n chunk retries. Non-zero
    // in the failed branch means the retries were exhausted → hard failure.
    let tusRetryCount = 0;
    // Log UPLOAD_INITIATED — creator passed all local checks and we're
    // about to start streaming bytes to the server.
    if (uploadSessionId) {
      logUploadEvent({
        axiosInstance,
        stage: UPLOAD_STAGES.UPLOAD_INITIATED,
        sessionId: uploadSessionId,
        fileMetadata: videoFile ? { name: videoFile.name, size: videoFile.size, type: videoFile.type } : null,
      });
    }
    const fileSizeMB = (videoFile.size || 0) / (1024 * 1024);
    const isLargeFile = fileSizeMB > 90; // backend compresses anything > 90MB

    // Stage simulator — runs while finalize is happening server-side
    let stageTimer = null;
    let elapsedTimer = null;
    const advanceFinalizeStages = () => {
      setProcessingStage('verify');
      const t1 = setTimeout(() => {
        setProcessingStage('audio');
        const t2 = setTimeout(() => {
          if (isLargeFile) {
            setProcessingStage('compress');
            const t3 = setTimeout(() => setProcessingStage('finalize'), Math.min(55000, 20000 + fileSizeMB * 60));
            stageTimer = t3;
          } else {
            setProcessingStage('finalize');
          }
        }, 10000);
        stageTimer = t2;
      }, 3000);
      stageTimer = t1;
    };

    try {
      // -------- NEW TUS DIRECT-TO-BUNNY UPLOAD FLOW (iter 107) --------
      // Old flow: chunked bytes streamed through /api/upload/chunk. Cloudflare
      // capped requests at 100 MB and dropped everything larger. New flow:
      //   1. POST metadata to /api/uploads/presign → get Bunny TUS creds
      //   2. tus-js-client PATCHes chunks straight to Bunny (Cloudflare-free)
      //   3. POST /api/uploads/tus-finalize with retry telemetry
      //   4. If creator uploaded a custom thumbnail image, POST it to the
      //      existing /videos/{id}/thumbnail endpoint (tiny file, no TUS)
      // Bunny webhook then flips status to 'approved' as before.

      // (1) Presign
      const presignResp = await axiosInstance.post('/uploads/presign', {
        title,
        genre,
        description,
        tools_used: toolsUsed,
        style: videoStyle,
        // Hook timing is set post-upload via the HookEditor on the Studio card
        // (not gathered at upload-time). Backend defaults hook_start_time to 0
        // and derives hook_end_time. Sending 0 here is intentional; the old
        // `hookStartTime || 0` referenced an undeclared variable and would
        // ReferenceError once JavaScript actually reached this expression.
        hook_start_time: 0,
        ai_disclosure: disclosure,
        file_metadata: { name: videoFile.name, size: videoFile.size, type: videoFile.type },
      });
      const creds = presignResp.data;
      if (!creds?.video_guid) {
        throw new Error('Presign returned no video_guid');
      }

      // (2) TUS upload — wait for it via a Promise wrapper so the try/catch
      // still catches upload errors and the finally block still fires.
      const uploadStart = Date.now();
      let tusController = null;
      await new Promise((resolve, reject) => {
        tusController = startTusUpload({
          file: videoFile,
          creds,
          onProgress: (pct) => setProgress(pct),
          onRetry: (n) => {
            tusRetryCount = n;
            if (n === 1) {
              toast.info('Connection hiccup — resuming automatically…', { duration: 3500 });
            }
          },
          onSuccess: () => resolve(),
          onError: (err, retryCount) => {
            tusRetryCount = retryCount;
            reject(err);
          },
        });
        // Allow the outer catch to abort if the component unmounts mid-upload.
        // (Not strictly needed for this fix but sets us up for future
        // cancellation UX without refactoring.)
        tusController._abortRef = tusController.abort;
      });
      // Bytes actually observed leaving the browser (from tus-js-client's
      // onProgress high-water mark). If this is < videoFile.size, the
      // upload was truncated — the backend will detect that against
      // Bunny's storageSize and return a specific failure.
      const bytesSent = typeof tusController?.getBytesSent === 'function'
        ? tusController.getBytesSent()
        : 0;

      // (3) Tell the backend the client thinks we're done + hand it the
      // retry count and observed byte count. The backend now cross-checks
      // Bunny's storageSize against `bytes_sent` + the presign-declared
      // file size, and short-circuits into a `failed` response if Bunny
      // received a truncated file or already flipped to Status 6.
      const elapsedMs = Date.now() - uploadStart;
      let finalizeResp = null;
      try {
        const _fr = await axiosInstance.post('/uploads/tus-finalize', {
          video_id: creds.video_id,
          chunk_retry_count: tusRetryCount,
          elapsed_ms: elapsedMs,
          bytes_sent: bytesSent,
        });
        finalizeResp = _fr?.data || null;
      } catch (_e) {
        // Network hiccup on the diagnostic call is non-fatal — Bunny's
        // webhook and the 5-min status reconciler will still catch a
        // real failure. Just don't block the UI.
      }

      // If the backend already knows the upload failed (truncated,
      // Status 6, etc.), surface the specific reason to the user right
      // here — no need to make them wait for the webhook to eventually
      // fail the video. This is the whole point of the inline Bunny
      // query in tus-finalize.
      if (finalizeResp && finalizeResp.success === false && finalizeResp.error) {
        // Log UPLOAD_FAILED to the funnel so admins can see the
        // truncation/codec verdict server-side too.
        if (uploadSessionId) {
          try {
            logUploadEvent({
              axiosInstance,
              stage: UPLOAD_STAGES.UPLOAD_FAILED,
              sessionId: uploadSessionId,
              videoId: creds.video_id,
              errorMsg: finalizeResp.error,
              fileMetadata: videoFile ? {
                name: videoFile.name,
                size: videoFile.size,
                type: videoFile.type,
                tus_retry_count: tusRetryCount,
                bunny_status_code: finalizeResp.bunny_status_code,
                storage_size: finalizeResp.storage_size,
                expected_size: finalizeResp.expected_size,
              } : null,
            });
          } catch (_e) { /* logging best-effort */ }
        }
        throw new Error(finalizeResp.error);
      }

      // (4) If a custom thumbnail was picked, PUT it now via the existing
      // small-file endpoint (well under any body-size limit).
      if (customThumbnailFile) {
        try {
          const fd = new FormData();
          fd.append('thumbnail', customThumbnailFile);
          await axiosInstance.post(`/videos/${creds.video_id}/thumbnail`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (thumbErr) {
          // Non-fatal — the video will use Bunny's auto-generated thumbnail
          // and the creator can retry via the Edit Details modal.
          console.warn('Custom thumbnail upload failed:', thumbErr);
          toast.error('Your custom thumbnail did not upload — the video is live using an auto-generated one. Edit the video to try the thumbnail again.', { duration: 6000 });
        }
      }

      const data = { id: creds.video_id, status: 'processing' };
      // ----------------- END TUS FLOW -----------------
      if (data.status === 'processing') {
        toast.success(
          data.message || "Upload received! Processing your video — it'll appear in Studio when ready.",
          { duration: 7000 }
        );
      } else if (data.status === 'pending_review') {
        toast.success(data.message || 'Received — awaiting admin review.', { duration: 7000 });
      } else if (data.compressed) {
        toast.success(
          `Optimized from ${data.compressed.original_mb} MB to ${data.compressed.compressed_mb} MB for faster streaming.`,
          { duration: 6000 }
        );
      } else {
        toast.success('Video uploaded successfully!', { duration: 3000 });
      }
      // Log UPLOAD_COMPLETED — end of the happy path. Include the video_id
      // returned by finalize so the admin funnel can correlate the event
      // chain to the resulting DB row. Include tus_retry_count so admins
      // can distinguish "clean upload" from "resumed and recovered" — a
      // completion with retries > 0 means the connection dropped mid-flight
      // but TUS auto-resumed and finished successfully (which is exactly
      // the resilience win we shipped this iteration for).
      if (uploadSessionId) {
        logUploadEvent({
          axiosInstance,
          stage: UPLOAD_STAGES.UPLOAD_COMPLETED,
          sessionId: uploadSessionId,
          videoId: data?.id || data?.video_id || null,
          fileMetadata: videoFile ? {
            name: videoFile.name,
            size: videoFile.size,
            type: videoFile.type,
            tus_retry_count: tusRetryCount,
          } : null,
        });
      }
      resetUploadForm();
      setShowUpload(false);
      fetchMyVideos();
      // Poll for processing → published transition so the user sees the
      // status badge update without manually refreshing.
      if (data.status === 'processing') {
        let polls = 0;
        const maxPolls = 30; // ~3 minutes
        const pollId = setInterval(async () => {
          polls += 1;
          await fetchMyVideos();
          if (polls >= maxPolls) clearInterval(pollId);
        }, 6000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(errorMsg);
      // Log UPLOAD_FAILED — categorize the error so admin sees "server_5xx"
      // vs "client_network" vs "bunny_401" at a glance in the funnel.
      // Backend responds to this event by firing an admin email inline.
      if (uploadSessionId) {
        let errType = 'unknown';
        const status = err?.response?.status;
        if (status === 401 || status === 403) errType = 'auth';
        else if (status === 400 || status === 413) errType = 'validation';
        else if (status && status >= 500) errType = 'server_5xx';
        else if (status && status >= 400) errType = `http_${status}`;
        else if (err?.code === 'ECONNABORTED') errType = 'client_timeout';
        // Classify programming errors (undeclared variable, wrong type, etc.)
        // separately from network drops. `client_network` gets false-positives
        // when a code bug throws BEFORE the request even leaves the browser —
        // and those failures LOOK identical to "user's wifi died" in the
        // event log unless we call them out. Any of these three JS-native
        // error classes means a bug in our code, not a network issue.
        else if (err instanceof ReferenceError || err instanceof TypeError || err instanceof SyntaxError) errType = 'client_code';
        else if (!err?.response) errType = 'client_network';
        // Append chunk-retry telemetry to the error message so admin can
        // tell "hard failed after 5 auto-retries" from "hard failed on first
        // attempt". Also stashed in file_metadata so filters can grep on it.
        const enrichedMsg = tusRetryCount > 0
          ? `${errorMsg} (after ${tusRetryCount} TUS chunk retry attempt${tusRetryCount === 1 ? '' : 's'})`
          : errorMsg;
        logUploadEvent({
          axiosInstance,
          stage: UPLOAD_STAGES.UPLOAD_FAILED,
          sessionId: uploadSessionId,
          errorType: errType,
          errorMessage: enrichedMsg,
          fileMetadata: videoFile ? {
            name: videoFile.name,
            size: videoFile.size,
            type: videoFile.type,
            tus_retry_count: tusRetryCount,
          } : null,
        });
      }
    } finally {
      if (stageTimer) clearTimeout(stageTimer);
      if (elapsedTimer) clearInterval(elapsedTimer);
      setProcessingStage(null);
      setProcessingElapsed(0);
      setUploading(false);
    }
  };

  return (
    <div className="pb-32 px-5 sm:px-6">
      {/* Header with eye icon */}
      <div className="py-6 sm:py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <img 
              src={EYE_ICON_URL} 
              alt="AUDIVUE" 
              className="w-12 h-12 rounded-xl shadow-lg hidden sm:block"
            />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Studio</h1>
              <p className="text-zinc-400 text-sm mt-0.5">{videos.length} video{videos.length !== 1 ? 's' : ''} uploaded</p>
            </div>
          </div>
          <Button
            onClick={() => {
              // Log ATTEMPT_STARTED when the panel opens (fresh attempt from
              // the creator's POV). Closing/reopening mints a new session
              // so a full-cancel retry doesn't pollute the previous
              // session's event timeline.
              const opening = !showUpload;
              if (opening) {
                const sid = newUploadSessionId();
                setUploadSessionId(sid);
                logUploadEvent({
                  axiosInstance,
                  stage: UPLOAD_STAGES.ATTEMPT_STARTED,
                  sessionId: sid,
                });
              }
              setShowUpload(opening);
            }}
            className="bg-white hover:bg-zinc-200 text-black rounded-full px-5 text-sm font-semibold"
            data-testid="toggle-upload-btn"
          >
            {showUpload ? <><ChevronUp className="w-4 h-4 mr-1.5" />Close</> : <><Upload className="w-4 h-4 mr-1.5" />Upload</>}
          </Button>
        </div>

        {/* Tab switcher: Videos / Analytics / Settings */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800 overflow-x-auto">
          <button
            onClick={() => setTab('videos')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === 'videos'
                ? 'border-white text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="studio-tab-videos"
          >
            <span className="inline-flex items-center gap-2">
              <Video className="w-4 h-4" />
              My Videos
            </span>
          </button>
          <button
            onClick={() => setTab('analytics')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === 'analytics'
                ? 'border-white text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="studio-tab-analytics"
          >
            <span className="inline-flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </span>
          </button>
          <button
            onClick={() => setTab('settings')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === 'settings'
                ? 'border-white text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="studio-tab-settings"
          >
            <span className="inline-flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </span>
          </button>
        </div>

        {tab === 'analytics' ? (
          <div className="-mx-5 sm:-mx-6">
            <ArtistAnalyticsPage />
          </div>
        ) : tab === 'settings' ? (
          <div className="-mx-5 sm:-mx-6">
            <ProfilePage embedded />
          </div>
        ) : (
        <>
        {/* Upload form — collapsible */}
        {showUpload && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-8 bg-[#111] border border-zinc-800 rounded-xl p-5 sm:p-6"
            data-testid="upload-section"
          >
            {/* Music | Film content-type toggle */}
            <div className="flex items-center gap-2 mb-5" data-testid="upload-kind-toggle">
              <button type="button" data-testid="upload-kind-music"
                onClick={() => setUploadKind('music')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${uploadKind === 'music' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
                <Music className="w-4 h-4" /> Music Video
              </button>
              <button type="button" data-testid="upload-kind-film"
                onClick={() => setUploadKind('film')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${uploadKind === 'film' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
                <Film className="w-4 h-4" /> Film
              </button>
            </div>

            {uploadKind === 'film' ? (
              <FilmUploadForm axiosInstance={axiosInstance} onUploaded={() => { setShowUpload(false); fetchMyVideos(); }} />
            ) : (
              <StudioMusicUploadForm
                artistName={artistName}
                axiosInstance={axiosInstance}
                canvasRef={canvasRef}
                captureFrame={captureFrame}
                capturedThumbnail={capturedThumbnail}
                clearVideo={clearVideo}
                contentCertified={contentCertified}
                currentTime={currentTime}
                customThumbnailFile={customThumbnailFile}
                customThumbnailInputRef={customThumbnailInputRef}
                description={description}
                disclosure={disclosure}
                formatTime={formatTime}
                genre={genre}
                handleCustomThumbnailPick={handleCustomThumbnailPick}
                handleSeek={handleSeek}
                handleTimeUpdate={handleTimeUpdate}
                handleUpload={handleUpload}
                handleVideoLoaded={handleVideoLoaded}
                handleVideoSelect={handleVideoSelect}
                isPlaying={isPlaying}
                musicSuggestedTags={musicSuggestedTags}
                processingElapsed={processingElapsed}
                processingStage={processingStage}
                progress={progress}
                rightsConfirmed={rightsConfirmed}
                setCapturedThumbnail={setCapturedThumbnail}
                setContentCertified={setContentCertified}
                setDescription={setDescription}
                setDisclosure={setDisclosure}
                setGenre={setGenre}
                setIsPlaying={setIsPlaying}
                setMusicSuggestedTags={setMusicSuggestedTags}
                setRightsConfirmed={setRightsConfirmed}
                setTitle={setTitle}
                setToolsUsed={setToolsUsed}
                setVideoStyle={setVideoStyle}
                tab={tab}
                thumbnailTimestamp={thumbnailTimestamp}
                title={title}
                togglePlay={togglePlay}
                toolsCatalog={toolsCatalog}
                toolsUsed={toolsUsed}
                uploading={uploading}
                useTimestampOnly={useTimestampOnly}
                useTimestampOnlySelected={useTimestampOnlySelected}
                videoDuration={videoDuration}
                videoInputRef={videoInputRef}
                videoPreview={videoPreview}
                videoPreviewRef={videoPreviewRef}
                videoStyle={videoStyle}
                videos={videos}
              />
            )}
          </motion.div>
        )}

        {/* My Videos - Better desktop grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-video bg-zinc-900 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16">
            <Video className="w-16 h-16 text-zinc-700 mx-auto mb-3" />
            <h3 className="text-lg text-zinc-400 mb-1">No videos yet</h3>
            <p className="text-zinc-500 text-sm">Click Upload above to add your first AI music video</p>
          </div>
        ) : (
          <StudioVideoGrid
            axiosInstance={axiosInstance}
            disclosure={disclosure}
            fetchMyVideos={fetchMyVideos}
            getThumbnail={getThumbnail}
            handleDelete={handleDelete}
            handleReconcile={handleReconcile}
            playVideo={playVideo}
            reconcilingId={reconcilingId}
            setEditDetailsVideo={setEditDetailsVideo}
            setRetagVideo={setRetagVideo}
            videos={videos}
          />
        )}
        {retagVideo && (
          <RetagDisclosureModal
            open={!!retagVideo}
            video={retagVideo}
            axiosInstance={axiosInstance}
            onClose={() => setRetagVideo(null)}
            onSaved={() => { fetchMyVideos(); }}
          />
        )}
        {editDetailsVideo && (
          <EditDetailsModal
            open={!!editDetailsVideo}
            video={editDetailsVideo}
            axiosInstance={axiosInstance}
            onClose={() => setEditDetailsVideo(null)}
            onSaved={() => { fetchMyVideos(); }}
          />
        )}
        </>
        )}
      </div>

      {/* Pre-upload warning for oversized/high-bitrate files. Non-blocking. */}
      <OversizeFileWarning
        open={!!oversizeWarning}
        stats={oversizeWarning}
        onProceed={() => {
          if (!oversizeWarning?.file) return;
          const file = oversizeWarning.file;
          setVideoFile(file);
          setVideoPreview(URL.createObjectURL(file));
          setCapturedThumbnail(null);
          setThumbnailTimestamp(1.0);
          setCurrentTime(0);
          setIsPlaying(false);
          setOversizeWarning(null);
        }}
        onPickDifferent={() => {
          setOversizeWarning(null);
          if (videoInputRef.current) videoInputRef.current.value = '';
        }}
      />
    </div>
  );
};
